import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  breakpoints,
  Checkbox,
  DrawerContext,
  Toaster,
} from '@notino/react-styleguide';
import { AdditionalServicesAvailability } from '@notino/web-tracking';

import ErrorBoundary from '@components/ErrorBoundary';
import PriceLabel from '@components/PriceLabel';
import { css } from '@linaria/core';

import { ICurrency } from '../../../../App/model';
import { CurrencyWrapper, PriceWrapper } from '../../styled';
import { useEngravingTryItFirstContext } from '../context/EngravingTryItFirst';

import { EngravingDrawer } from './EngravingDrawer';
import { messages } from './engravingMessages';
import { Container, Description, Edit, DisabledTextWrapper } from './styled';

interface IEngravingProps {
  imageAlt: string;
  code: string;
  currency: ICurrency;
  maxChars: number;
  price: number;
  image?: string;
  engravingAvailable?: AdditionalServicesAvailability;
}

export const Engraving: React.FC<IEngravingProps> = React.memo(
  ({
    price,
    currency,
    maxChars,
    image,
    imageAlt,
    code,
    engravingAvailable,
  }) => {
    const { formatMessage } = useIntl();
    const {
      state: { withTryItFirst, withEngraving, engravingInitials },
      actions: { changeEngravingInitials },
    } = useEngravingTryItFirstContext();

    const { openDrawer } = DrawerContext.useDrawerContext();

    const showDrawer = () => {
      openDrawer(
        <EngravingDrawer
          maxChars={maxChars}
          image={image}
          imageAlt={imageAlt}
          code={code}
          changeEngravingInitials={changeEngravingInitials}
          engravingInitials={engravingInitials}
        />,
        {
          initialPosition: 'dynamic',
          isTitleBorderHidden: true,
          hideScrollbar: true,
          showFade: false,
          headerCustomStyles: css`
            display: none;
          `,
          drawerContainerCustomStyles: css`
            @media (min-width: ${breakpoints.md}) {
              [data-testid='drawer-content'],
              [data-testid='drawer-scrollable-content'] {
                max-height: 100dvh !important;
              }
            }
          `,
        }
      );
    };

    const handleUnCheck = () => {
      Toaster.info({
        content: formatMessage(messages.removedNotificationText),
        icon: 'IconRegularCheckmark',
      });
      changeEngravingInitials('');
    };

    const handleCheckbox = ({
      target,
    }: React.ChangeEvent<HTMLInputElement>) => {
      const checked = target.checked;
      if (!checked) {
        handleUnCheck();
      } else {
        showDrawer();
      }
    };

    const isEngravingDisabled =
      engravingAvailable !== AdditionalServicesAvailability.available ||
      withTryItFirst;

    return (
      <ErrorBoundary>
        <Container data-testid="pd-engraving" id="pdEngraving">
          <DisabledTextWrapper disabled={isEngravingDisabled}>
            <Checkbox
              disabled={isEngravingDisabled}
              checked={withEngraving}
              onChange={handleCheckbox}
              tabIndex={0}
              position={0}
            >
              {withEngraving ? (
                <div>
                  <FormattedMessage
                    {...messages.engravingCheckboxCheckedLabel}
                    values={{
                      initials: engravingInitials,
                    }}
                  />
                  <Edit
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.preventDefault();
                      showDrawer();
                    }}
                  >
                    <FormattedMessage {...messages.engravingEdit} />
                  </Edit>
                </div>
              ) : (
                <FormattedMessage
                  {...messages.engravingCheckboxLabel}
                  values={{
                    price: (
                      <PriceLabel
                        price={{ value: price, currency: currency.code }}
                        wrapperComponent={PriceWrapper}
                        currencyComponent={CurrencyWrapper}
                      />
                    ),
                  }}
                />
              )}
            </Checkbox>

            {!withEngraving && (
              <Description>
                <FormattedMessage
                  {...messages.engravingCheckboxLabelDesc}
                  values={{
                    a: (chunks: React.ReactNode) => (
                      <a
                        href={formatMessage(messages.engravingSpecialPageHref)}
                        rel="noreferrer"
                        target="_blank"
                      >
                        {chunks}
                      </a>
                    ),
                  }}
                />
              </Description>
            )}
          </DisabledTextWrapper>
        </Container>
      </ErrorBoundary>
    );
  }
);

Engraving.displayName = 'Engraving';
